import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/components/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  comorbidites,
  typeHLHOptions,
  lateralisationLesionOptions,
  localisationLesionOptions,
  typeLesionOptions,
  lateralityOptions,
} from "@/data/fichePatientData";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { PatientData } from "@/data/seancesData";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const formSchema = z.object({
  lastName: z.string().min(1, { message: "Le nom est requis." }).optional(),
  firstName: z.string().min(1, { message: "Le prénom est requis." }).optional(),
  email: z.string().email({ message: "Doit être un email valide." }).optional(),
  phoneNumber: z.string().optional(),
  patientFiles: z
    .object({
      gender: z.enum(["HOMME", "FEMME", "AUTRE"]).nullable().optional(),
      birthDate: z.date().optional(),
      injuryDate: z.date().optional(),
      readingImpairment: z.enum(["OUI", "NON"]).nullable().optional(),
      injuryType: z
        .enum(["ISCHEMIQUE", "HEMORRAGIQUE", "AUTRES"])
        .nullable()
        .optional(),
      injuryTypePrecisions: z.string().optional(),
      injuryLateralization: z
        .enum(["GAUCHE", "DROITE", "BILATERAL"])
        .nullable()
        .optional(),
      injuryLocation: z
        .array(
          z.enum([
            "PARIETAL",
            "OCCIPITAL",
            "FRONTAL",
            "TEMPORAL",
            "NOYAUX_CENTRAUX",
            "AUTRES",
          ])
        )
        .optional(),
      injuryLocationPrecisions: z.string().optional(),
      hlhType: z
        .enum([
          "TOTALE_DROITE",
          "TOTALE_GAUCHE",
          "BILATERALE",
          "QSD",
          "QID",
          "QSG",
          "QIG",
        ])
        .nullable()
        .optional(),
      comorbidities: z
        .array(
          z.enum([
            "NEGLIGENCE",
            "APHASIE",
            "ALEXIE",
            "AGNOSIE",
            "SIMULTAGNOSIE",
            "TROUBLE_EXECUTIF",
            "OPHTALMIQUE",
          ])
        )
        .optional(),
      neurologicalAntecedents: z.enum(["OUI", "NON"]).optional(),
      laterality: z
        .enum(["GAUCHE", "DROITE", "AMBIDEXTRE"])
        .nullable()
        .optional(),
      lateralityPrecisions: z.string().optional(),
      neurologicalAntecedentsPrecisions: z.string().optional(),
    })
    .optional(),
});

const PatientCardComponent: React.FC = () => {
  const { patientId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      email: "",
      phoneNumber: "",
      patientFiles: {
        gender: null,
        birthDate: undefined,
        injuryDate: undefined,
        readingImpairment: undefined,
        injuryType: undefined,
        injuryTypePrecisions: "",
        injuryLateralization: undefined,
        injuryLocation: [],
        injuryLocationPrecisions: "",
        hlhType: undefined,
        comorbidities: [],
        neurologicalAntecedents: undefined,
        neurologicalAntecedentsPrecisions: "",
        laterality: null,
        lateralityPrecisions: "",
      },
    },
  });

  useEffect(() => {
    if (patientId) {
      axios.get(`/practitioner/patient/${patientId}`).then((response) => {
        const patientData = response.data;

        const birthDate = patientData.patientFiles?.birthDate
          ? new Date(patientData.patientFiles.birthDate)
          : new Date();
        const injuryDate = patientData.patientFiles.birthDate
          ? new Date(patientData.patientFiles.injuryDate)
          : new Date();

        form.reset({
          lastName: patientData.lastName,
          firstName: patientData.firstName,
          email: patientData.email,
          phoneNumber: patientData.phoneNumber,
          patientFiles: {
            gender: patientData.patientFiles.gender,
            birthDate: birthDate,
            injuryDate: injuryDate,
            readingImpairment: patientData.patientFiles.readingImpairment,
            injuryType: patientData.patientFiles.injuryType,
            injuryTypePrecisions: patientData.patientFiles.injuryTypePrecisions,
            injuryLateralization: patientData.patientFiles.injuryLateralization,
            injuryLocation: patientData.patientFiles.injuryLocation,
            injuryLocationPrecisions:
              patientData.patientFiles.injuryLocationPrecisions,
            hlhType: patientData.patientFiles.hlhType,
            comorbidities: patientData.patientFiles.comorbidities,
            neurologicalAntecedents:
              patientData.patientFiles.neurologicalAntecedents,
            neurologicalAntecedentsPrecisions:
              patientData.patientFiles.neurologicalAntecedentsPrecisions,
            laterality: patientData.patientFiles.laterality,
            lateralityPrecisions:
              patientData.patientFiles.lateralityPrecisions,
          },
        });
        setIsLoading(false);
        setPatientData(patientData);
      });
    } else {
      setIsLoading(false);
    }
  }, [patientId, form]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  async function handleSave(data: z.infer<typeof formSchema>) {
    try {
      if (patientId) {
        await axios.put(`/practitioner/patient/${patientId}`, data);
        toast.success("Patient mis à jour");
      } else {
        await axios.post(`/practitioner/patient`, data);
        toast.success("Patient créé");
      }
    } catch (error) {
      // Vérification et gestion de l'erreur lors de la requête Axios
      if (axios.isAxiosError(error) && error.response) {
        // Afficher un message personnalisé si besoin, ou ne rien afficher
        console.error("Erreur lors de la requête Axios :", error.response.data);
      } else {
        console.error(
          "Erreur inconnue lors de la sauvegarde du patient",
          error
        );
      }
      if (patientId) {
        toast.error("Erreur lors de la mise à jour du patient");
      } else {
        toast.error("Erreur lors de la création du patient");
      }
    }
  }

  return (
    <Card className="w-5/6 min-h-5/6 mt-11 mb-11">
      <CardHeader className="border-b mb-6 w-full flex">
        <CardTitle>
          {patientData
            ? `${patientData.firstName} ${patientData.lastName}`
            : "Nouveau Patient"}
        </CardTitle>
        <CardDescription>Informations sur le patient</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <CardContent>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Prénom</FormLabel>
                    <FormControl>
                      <Input placeholder="Prenom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="nom@mail.fr" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <Input placeholder="06/07 .. .. .. .." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.gender"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Genre</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="HOMME" value="HOMME">
                            Homme
                          </SelectItem>
                          <SelectItem key="FEMME" value="FEMME">
                            Femme
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.birthDate"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Date de naissance</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "dd MMMM yyyy", {
                                locale: fr,
                              })
                            ) : (
                              <span>Choisir une date</span>
                            )}

                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date() || date < new Date("1920-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="patientFiles.injuryDate"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Date de la lésion</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "dd MMMM yyyy", {
                                locale: fr,
                              })
                            ) : (
                              <span>Choisir une date</span>
                            )}

                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date() || date < new Date("1920-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.injuryLateralization"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Latéralisation lésion</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une option" />
                        </SelectTrigger>
                        <SelectContent>
                          {lateralisationLesionOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.display}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.readingImpairment"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Gène à la lecture</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="OUI" value="OUI">
                            Oui
                          </SelectItem>
                          <SelectItem key="NON" value="NON">
                            Non
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="patientFiles.neurologicalAntecedents"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Antécédents Neurologique</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="OUI" value="OUI">
                            Oui
                          </SelectItem>
                          <SelectItem key="NON" value="NON">
                            Non
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="patientFiles.injuryType"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Type de lésion</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                        className="flex flex-col justify-center"
                      >
                        {typeLesionOptions.map((option) => (
                          <FormItem
                            key={option.value}
                            className="flex items-center space-x-3 space-y-0"
                          >
                            <FormControl>
                              <RadioGroupItem value={option.value} />
                            </FormControl>
                            <FormLabel>{option.display}</FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.injuryLocation"
                render={() => (
                  <FormItem className="w-1/4">
                    <FormLabel>Localisation de la lésion</FormLabel>
                    {localisationLesionOptions.map((option) => (
                      <FormField
                        key={option.value}
                        control={form.control}
                        name="patientFiles.injuryLocation"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={option.value}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(
                                    option.value as
                                      | "PARIETAL"
                                      | "OCCIPITAL"
                                      | "FRONTAL"
                                      | "TEMPORAL"
                                      | "NOYAUX_CENTRAUX"
                                      | "AUTRES"
                                  )}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([
                                          ...(field.value || []),
                                          option.value,
                                        ])
                                      : field.onChange(
                                          (field.value || []).filter(
                                            (value: string) =>
                                              value !== option.value
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {option.display}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-around mb-6">
              <div className="w-1/4">
                {form.watch("patientFiles.neurologicalAntecedents") ===
                  "OUI" && (
                  <FormField
                    control={form.control}
                    name="patientFiles.neurologicalAntecedentsPrecisions"
                    render={({ field }) => (
                      <FormItem className="">
                        <FormLabel>Préciser les antécédents</FormLabel>
                        <FormControl>
                          <Input placeholder="Préciser" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <div className="w-1/4">
                {" "}
                {form.watch("patientFiles.injuryType") === "AUTRES" && (
                  <FormField
                    control={form.control}
                    name="patientFiles.injuryTypePrecisions"
                    render={({ field }) => (
                      <FormItem className="">
                        <FormLabel>Préciser le type de lésion</FormLabel>
                        <FormControl>
                          <Input placeholder="Préciser" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <div className="w-1/4">
                {" "}
                {form
                  .watch("patientFiles.injuryLocation")
                  ?.includes("AUTRES") && (
                  <FormField
                    control={form.control}
                    name="patientFiles.injuryLocationPrecisions"
                    render={({ field }) => (
                      <FormItem className="">
                        <FormLabel>Préciser la localisation</FormLabel>
                        <FormControl>
                          <Input placeholder="Préciser" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="patientFiles.hlhType"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Type d'HLH (dernière atteinte)</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                        className="flex flex-col justify-center"
                      >
                        {typeHLHOptions.map((option) => (
                          <FormItem
                            key={option.value}
                            className="flex items-center space-x-3 space-y-0"
                          >
                            <FormControl>
                              <RadioGroupItem
                                key={option.value}
                                value={option.value}
                              />
                            </FormControl>
                            <FormLabel>{option.display}</FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="patientFiles.comorbidities"
                render={() => (
                  <FormItem className="w-1/4">
                    <FormLabel>Comorbidités</FormLabel>
                    {comorbidites.map((comorbidite) => (
                      <FormField
                        key={comorbidite.value}
                        control={form.control}
                        name="patientFiles.comorbidities"
                        render={({ field }) => {
                          return (
                            <FormItem
                              key={comorbidite.value}
                              className="flex flex-row items-start space-x-3 space-y-0"
                            >
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(
                                    comorbidite.value as
                                      | "NEGLIGENCE"
                                      | "APHASIE"
                                      | "ALEXIE"
                                      | "AGNOSIE"
                                      | "SIMULTAGNOSIE"
                                      | "TROUBLE_EXECUTIF"
                                      | "OPHTALMIQUE"
                                  )}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([
                                          ...(field.value || []),
                                          comorbidite.value,
                                        ])
                                      : field.onChange(
                                          (field.value || []).filter(
                                            (value: string) =>
                                              value !== comorbidite.value
                                          )
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {comorbidite.display}
                              </FormLabel>
                            </FormItem>
                          );
                        }}
                      />
                    ))}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="w-1/4">
              <FormField
                control={form.control}
                name="patientFiles.laterality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latéralité</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        // @ts-expect-error : it works
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une option" />
                        </SelectTrigger>
                        <SelectContent>
                          {lateralityOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.display}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
                              {form.watch("patientFiles.laterality") ===
                  "AMBIDEXTRE" && (
                  <FormField
                    control={form.control}
                    name="patientFiles.lateralityPrecisions"
                    render={({ field }) => (
                      <FormItem className="">
                        <FormLabel>Précisions sur la latéralité</FormLabel>
                        <FormControl>
                          <Input placeholder="Préciser" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center border-t">
            <Button
              type="submit"
              className="mt-6"
              onClick={() => {
                // console.log(form.formState.errors);
              }}
            >
              <Save className="mr-2" /> Enregistrer
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};
export default PatientCardComponent;
